
import { DatePicker, DateTimePicker, LoadingButton } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import { CircularProgress, Dialog, FormLabel, Grid, InputAdornment, Stack, Toolbar } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from "@mui/material/Paper";
import Select from '@mui/material/Select';
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ruLocale from "date-fns/locale/ru";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, useLocation } from "react-router";
import { Document, Page } from "react-pdf";

import useDialogs from "../../components/Dialog/useDialogs";
import RemovePopup from "../../components/Popups/RemovePopup";
import DoctorField from "../../components/Relations/DoctorField";
import EmployeeField from "../../components/Relations/EmployeeField";
import ResearchListField from "../../components/Relations/ResearchListField";
import { ContractSelect, DepartmentSelect, DivisionSelect, FilialDivisionSelect2, FilialSelect } from "../../components/Select/domainSelects";
import { checkRoles } from "../../functions/checkRoles";
import useLoader from "../../hooks/useLoader";
import { actCRUD, protocolCRUD } from "../../http/CRUD";
import DashboardLayout from "../../layouts/Dashboard";
import { getActForms } from "../../redux/actions/actFormActions";
import { addProtocol } from "../../redux/actions/protocolActions";
import { REQUEST_STATE_ID_COMPLETED, REQUEST_STATE_ID_CREATED, REQUEST_STATE_ID_DECLINED, REQUEST_STATE_ID_PROBESELECTION, REQUEST_STATE_ID_PROCESSING, REQUEST_STATE_ID_RESEARCH } from "../../redux/constants/requestStateConstants";
import CreateProtocol from "../Protocol/Popups/CreateProtocol";
import DirectionPDF from "./DirectionPDF";
import DirectionResult from './DirectionResult'
import { toUIDate } from "../../functions/dateHelpers";
import { RequestStateChecker } from "../../functions";
import { FormSection } from "../../components";
import ResultsAndProtocolsSection from "../../sections/ResultsAndProtocolsSection";
import useReload from "../../hooks/useReload";
import EditResultAndProtocolSection from "../../sections/EditResultAndProtocolSection";
import useRoles from "../../hooks/useRoles";
import PatientField from "../../components/Relations/PatientField";
import { organizationIds } from "../../models/organizationIds";
import { commentDialog } from "../../components/Dialog/CommentDialog";
import qs from "qs";
import { useForm, FormProvider as RHFFormProvider } from "react-hook-form";
import RHFIndicator from "../../components/hook-form/RHFIndicator";
import RHFTechnicalRegulations from "../../components/hook-form/RHFTechnicalRegulations";
import { orderTypeIds } from "../../models/orderTypeIds";
import { asJSONSafe } from "../../functions";
import { BasicTable } from "../../components";
import { serviceCRUD } from "../../http/CRUD";
import { SERVICE_TYPE_GOVERNMENT, SERVICE_TYPE_PAYED } from "../../redux/constants/serviceTypeConstants";
import { hasSignedProtocol } from "../../models/protocolStateIds";
import { fillDirectionDataFromPatient } from "../../functions/mapping/directionPatient";

const withDirectionDetail = (DirectionForm, DirectionPDFGenerator, directionFormKazakhName, directionFormName, directionFormNumber, directionName) => {
  return () => {
    const { alert, DIALOG } = useDialogs();
    const { t } = useTranslation();
    const actForm = useSelector(state => state.actForm)
    const { id } = useParams();
    const isNew = useMemo(() => !(id > 0), [id]);
    const [data, setData] = useState({});
    const { start: startSubmit, stop: stopSubmit, loading: loadingSubmit } = useLoader(false);
    const { start: workflowStart, stop: workflowStop, loading: workflowLoading } = useLoader(false);
    const style = { marginLeft: 10 }
    const stateCode = data.state?.code;

    // Form context for Indicators (RHFIndicator)
    const indicatorsMethods = useForm({
        defaultValues: { indicators: [] }
    });
    const lastIndicatorsFromOuterRef = useRef();
    const lastIndicatorsFromFormRef = useRef();

    const areEqual = (a, b) => {
        try {
            return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
        } catch {
            return a === b;
        }
    };

    // Keep RHF form in sync when outer state changes (guarded)
    useEffect(() => {
        const incoming = asJSONSafe(data?.indicators) || [];
        if (areEqual(incoming, lastIndicatorsFromOuterRef.current)) return;
        lastIndicatorsFromOuterRef.current = incoming;
        if (!areEqual(incoming, indicatorsMethods.getValues('indicators'))) {
            indicatorsMethods.reset({ indicators: incoming });
        }
    }, [data?.indicators, indicatorsMethods]);

    // Propagate RHFIndicator changes back to outer state (guarded)
    useEffect(() => {
        const subscription = indicatorsMethods.watch((values) => {
            const indicators = values?.indicators ?? [];
            if (areEqual(indicators, lastIndicatorsFromFormRef.current)) return;
            lastIndicatorsFromFormRef.current = indicators;
            if (!areEqual(indicators, data?.indicators)) {
                setData((prev) => ({ ...prev, indicators }));
            }
        });
        return () => subscription.unsubscribe();
    }, [indicatorsMethods, data?.indicators]);

    const location = useLocation();
    const queryParams = qs.parse(location.search?.replace('?', ''));
    const orderType = useMemo(() => {
        const fromQuery = queryParams.orderType !== undefined ? Number(queryParams.orderType) : 1;
        return isNew ? fromQuery : (data?.orderType ?? fromQuery);
    }, [isNew, queryParams.orderType, data?.orderType]);

    // Specifications for Services (built from data.indicators for government orderType)
    const [specifications, setSpecifications] = useState([]);
    const buildSpecificationsFromIndicators = useCallback(async (incomingIndicators) => {
        try {
            let indicators = Array.isArray(incomingIndicators) ? incomingIndicators : [];
            if (!indicators.length) {
                setSpecifications([]);
                return;
            }
            const version = indicators.length > 0 && indicators[0]?.indicators ? 2 : 1;
            if (version === 2) {
                indicators = indicators.flatMap(_ => _?.indicators || []);
            }
            indicators = indicators.filter(_ => _?.checked);
            if (!indicators.length) {
                setSpecifications([]);
                return;
            }
            const indicatorsPayload = indicators.map(_ => ({ groupId: _.groupId, subGroupName: _.name }));
            const services = (await serviceCRUD.post('indicators', indicatorsPayload, 'services'))
                .filter(_ => _.typeId == 2 && !_.isDeleted);
            const mapped = services.map(_ => ({
                id: _.id,
                service: _,
                department: _.department,
                departmentId: _.departmentId,
                division: _.division,
                divisionId: _.divisionId,
                serviceKind: _.kind,
                schedule: _.schedule,
                group: _.group,
                object: _.object,
                unit: _.unit,
                price: _.price,
                quantity: 0,
                inconsistenceQuantity: 0,
                count: 0,
                contractCount: 0
            }));
            setSpecifications(mapped);
        } catch (e) {
            console.error(e);
            setSpecifications([]);
        }
    }, []);

    useEffect(() => {
        if (orderType == orderTypeIds.government) {
            const indicators = asJSONSafe(data?.indicators) || [];
            buildSpecificationsFromIndicators(indicators);
        } else {
            setSpecifications([]);
        }
    }, [orderType, data?.indicators, buildSpecificationsFromIndicators]);

    const { isCommittee } = useRoles();

    const state = useMemo(
      () => new RequestStateChecker(data?.state?.code),
      [data?.state?.code]
    );

    const { reload, reloadValue } = useReload();

    useEffect(() => {
      if (actForm.actForms.length === 0) {
          dispatch(getActForms({paging: {skip: 0}}));
      }
    }, [actForm.actForms.length]);

    const handleChange = useCallback((key, value) => {
      setData({ ...data, [key]: value })
    }, [data]);
    const handleChangeObject = useCallback(
      (key, value) => setData(prev => ({ ...prev, [key]: value, [`${key}Id`]: value.id })),
      [data]
    );
    const handleChangePatient = useCallback((patient) => {
      setData(prev => ({
        ...prev,
        targetPatient: patient,
        targetPatientId: patient?.id ?? null,
        data: fillDirectionDataFromPatient(prev.data, patient),
      }));
    }, []);
    const handleChangeMulti = useCallback((obj) => {
      setData({ ...data, ...obj })
    }, [data]);

    const handleChangeData = useCallback((key, value, key2) => {
      setData({ ...data, data: { ...data.data, [key]: { 'key': key2, value } } })
    }, [data]);

    const handleChangeSample = useCallback((key, value, i, key2) => {
      const array = data.data?.samples?.value.slice(0)
      array[i][key] = { 'key': key2, value }
      setData({
        ...data, data: {
          ...data.data,
          samples: { key: 'samples', value: array }
        }
      })
    }, [data]);

    const deleteSample = useCallback((index) => {
      setData({ ...data, data: { ...data.data, samples: { key: 'samples', value: data.data?.samples?.value.filter((el, i) => index !== i) } } })
    }, [data]);

    const dispatch = useDispatch();
    const { push } = useHistory();

    const schema = {}

    const { loading: fetchLoading, start: fetchStart, stop: fetchStop } = useLoader(true);
    const fetch = useCallback(() => {
      if (isNew) {
        fetchStop();
        return;
      }
      fetchStart();
      actCRUD.get(id)
        .then((data) => {
          let actData = typeof data.data === 'string' ? JSON.parse(data.data) : {}
          setData({ ...data, data: { ...actData } });
        })
        .catch(alert)
        .finally(fetchStop)
    }, [id, fetchStart, fetchStop, isNew]);
    useEffect(fetch, [fetch]);

    const submit = useCallback((silent) => new Promise((resolve, reject) => {
      if (REQUEST_STATE_ID_COMPLETED === stateCode) {
        resolve();
        return;
      }

      if (!data.filialId) {
        alert('Филиал нужно заполнить!');
        return
      }
      const actFormObj = actForm.actForms.find(a => {
        return a.number === directionName
      })
      const newData = { ...data }
      delete newData.responsible
      delete newData.responsibleForProbes
      delete newData.performer
      if (!silent)
        startSubmit();
      debugger;
      (isNew
        ? actCRUD.create({
          createdAt: new Date(),
          number: directionName,
          schema,
          ...newData,
          form: actFormObj,
          formId: actFormObj.id,
          orderType: orderType
        })
        : actCRUD.edit({
          id: id,
          number: directionName,
          schema,
          ...newData,
          form: actFormObj,
          formId: actFormObj.id,
          orderType: orderType
        })
      )
        .then((response) => {
          if (isNew && response.id) {
            push(`/${directionName}Detail/${response.id}`)
          } else {
            fetch();
          }
          resolve();
          // onSubmit();
        })
        .catch((error) => {
          reject();
          alert(error);
        })
        .finally(() => {
          if (!silent)
            stopSubmit();
        })        
    }), [data, startSubmit, stopSubmit, isNew, fetch, push, actForm]);

    const setStatus = (lastStateCode, nextStateCode, thenAction = fetch, comment) => {
      const validates = [];
      const validateNull = (key, label) => {
        if (!data[key]) validates.push(label + " нужно заполнить!");
      };
      if (nextStateCode === REQUEST_STATE_ID_PROBESELECTION) {
        validateNull("responsibleForProbesId", "Ответственный за отбор проб");
      } else if (nextStateCode === REQUEST_STATE_ID_RESEARCH) {
        //validateNull("performerId", "Лаборант");
      } else if (nextStateCode === REQUEST_STATE_ID_PROCESSING) {
        validateNull("responsibleForProbesId", "Ответственный за отбор проб");
        validateNull("departmentId", "Лаборатория");
        validateNull("responsibleId", "Заведующий лаборатории/Начальник отдела");
      } else if (nextStateCode === REQUEST_STATE_ID_COMPLETED) {
        validateNull("researchEndDateTime", "Дата завершения исследования");
      }
      if (validates.length > 0) {
        alert(validates.join("\n"));
        return;
      }
      workflowStart();
      submit(true)
        .then(() => actCRUD.workflowRun(id, lastStateCode, nextStateCode, comment))
        .then(updatedState => {
          if (updatedState.isRequestLocked) {
            const researchEndDateTime = data.researchEndDateTime;
            let monthString = 'месяц';
            try {
              const parsedResearchEndDateTime = new Date(researchEndDateTime);
              monthString = parsedResearchEndDateTime.toLocaleString('default', { month: 'long' });
            } catch (e) {
              console.error(e);
            }
            alert(`Тарификатор (${monthString}) заблокирован`);
          } else if (updatedState.isProtocolsNotSigned) {
            alert(`Протокол не подписан, для завершения заявки необходимо подписать протокол`);
          }
        })
        .then(thenAction)
        .catch(alert)
        .finally(workflowStop);
    };

    const hasResults = useMemo(() => data.results && data.results.length > 0, [data]);
    const hasProtocols = useMemo(() => data.protocols && data.protocols.length > 0, [data]);
    const rejectBlockedBySignedProtocol = useMemo(
      () => hasSignedProtocol(data?.protocols ?? []),
      [data?.protocols]
    );

    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);

    const onDocumentLoadSuccess = ({ numPages }) => {
      setNumPages(numPages);
    };

    const [protocolAddOpen, setProtocolAddOpen] = useState(false);
    const [protocolViewPdf, setProtocolViewPdf] = useState(false);
    const [protocolViewOpen, setProtocolViewOpen] = useState(false);
    const [createResultOpen, setCreateResultOpen] = useState(false);
    const [deleteActOpen, setDeleteActOpen] = useState(false);
    const [editResultOpen, setEditResultOpen] = useState(false);
    const [editResultId, setEditResultId] = useState(null);

    const [editProtocolOpen, setEditProtocolOpen] = useState(false);
    const [editProtocol, setEditProtocol] = useState(null);

    const handleEditResultOpen = async (resultId) => {
      setEditResultId(resultId);
      setEditResultOpen(true);
    }

    const handleEditProtocolOpen = async (protocolId) => {
      await startSubmit();

      const protocol = await protocolCRUD.get(protocolId);
      await setEditProtocol(protocol);
      await setEditProtocolOpen(true);

      await stopSubmit();
    }

    useEffect(() => {
      if (actForm.actForms.length === 0) {
        dispatch(getActForms({ paging: { skip: 0, take: 25 } }))
      }
    }, [actForm.actForms.length])

    const [isDisabledData, setIsDisabledData] = useState(false)
    const [isDisabledPerformer, setIsDisabledPerformer] = useState(false)
    useEffect(() => {        
      const isAdministrator = checkRoles('SuperAdministrator');
      const returnIsDisabledData = () => {
        return ([REQUEST_STATE_ID_PROCESSING, REQUEST_STATE_ID_RESEARCH, REQUEST_STATE_ID_DECLINED, REQUEST_STATE_ID_COMPLETED].includes(stateCode) || !checkRoles('SuperAdministrator', 'Administrator', 'Specialist', "Committee", "CommitteeAdministrator")) && !isAdministrator
      }
      const returnIsDisabledPerformer = () => {
        if (checkRoles('Supervisor') && [REQUEST_STATE_ID_RESEARCH].includes(stateCode))
          return false;

          return ([REQUEST_STATE_ID_RESEARCH, REQUEST_STATE_ID_DECLINED, REQUEST_STATE_ID_COMPLETED].includes(stateCode) || !checkRoles('SuperAdministrator', 'Administrator', 'Supervisor', "Committee", "CommitteeAdministrator")) && !isAdministrator
      }
      setIsDisabledData(returnIsDisabledData())
      setIsDisabledPerformer(returnIsDisabledPerformer())
    }, [stateCode])

    const addSample = useCallback(() => {
      data.data?.samples?.value
        ? setData({ ...data, data: { ...data.data, samples: { key: 'samples', value: [...data.data?.samples?.value, {}] } } })
        : setData({ ...data, data: { ...data.data, samples: { key: 'samples', value: [{}] } } })
    }, [data]);

    const renderDateField = (key, label, xs = 12) => {
      return (
        <Grid item xs={xs}>
          <FormControl component="fieldset">
            {label && <FormLabel component="legend">{label}</FormLabel>}
            <TextField
              type={"date"}
              margin="normal"
              value={toUIDate(data[key])}
              label="Дата"
              onChange={(e) => handleChange(key, e.target.value)}
              fullWidth
              id={key}
              name={key}
            />
          </FormControl>
        </Grid>
      );
    };

    return (
      <DashboardLayout style={{ display: "flex", flexDirection: "column" }}>
        <DIALOG />
        <>
        <Toolbar>
        <Button
          // style={{
          //   marginBottom: '10px'
          // }}
          variant="contained"
          type="button"
          onClick={() => { push(`/${directionName}`) }}
        >
          Вернуться
        </Button>
        {REQUEST_STATE_ID_COMPLETED === stateCode && checkRoles('SuperAdministrator', 'Administrator') && <LoadingButton style={style} variant={"contained"} loading={workflowLoading}
                    onClick={() => setStatus(REQUEST_STATE_ID_COMPLETED, REQUEST_STATE_ID_RESEARCH)}>Откатить</LoadingButton>}
        </Toolbar>
        </>
        {fetchLoading
          ? <CircularProgress />
          : <>
            {!isNew && <>{stateCode != REQUEST_STATE_ID_PROCESSING && <FormSection heading={"Журналы и протоколы"} mb={4}>
                <ResultsAndProtocolsSection
                  results={data?.results}
                  protocols={data?.protocols}
                  onResultClick={async (_) => {
                    await handleEditResultOpen(_);
                  }}
                  onProtocolClick={async (_) => {
                    await handleEditProtocolOpen(_);
                  }}
                  resultsReloadValue={reloadValue}
                  fetch={fetch}
                  startLoading={async () => {
                    await startSubmit();
                  }}
                  stopLoading={async () => {
                    await stopSubmit();
                  }}
                />
              </FormSection>}
            <DirectionPDF data={data} name={directionFormName} kazakhName={directionFormKazakhName}>
              <DirectionPDFGenerator data={data} name={directionFormName} kazakhName={directionFormKazakhName} />
            </DirectionPDF></>}
            <Box component="form" onSubmit={submit}>
              <Paper style={{ marginBottom: '15px' }}>
                <Toolbar>
                  <Typography style={{ marginRight: "auto" }} variant={"h6"}>Статус направления: {data.state?.name || "создаётся"}</Typography>
                  {/* {REQUEST_STATE_ID_CREATED === stateCode && checkRoles('SuperAdministrator', 'Administrator', 'Specialist', "Committee", "CommitteeAdministrator") && <LoadingButton style={style} variant={"contained"} loading={workflowLoading} 
                  onClick={() => setStatus(REQUEST_STATE_ID_CREATED, REQUEST_STATE_ID_PROBESELECTION)}>Отбор Пробы</LoadingButton>} */}
                  {REQUEST_STATE_ID_CREATED === stateCode && checkRoles('SuperAdministrator', 'Administrator', 'Specialist', "Committee", "CommitteeAdministrator") && <LoadingButton style={style} variant={"contained"} loading={workflowLoading}
                    onClick={() => setStatus(REQUEST_STATE_ID_CREATED, REQUEST_STATE_ID_PROCESSING)}>Отправить в лабораторию</LoadingButton>}
                  {/* {REQUEST_STATE_ID_PROBESELECTION === stateCode && checkRoles('SuperAdministrator', 'Administrator', 'Specialist') && <LoadingButton style={style} variant={"contained"} loading={workflowLoading} 
                  onClick={() => setStatus(REQUEST_STATE_ID_PROBESELECTION, REQUEST_STATE_ID_PROCESSING)}>Отправить в лабораторию</LoadingButton>} */}
                  {REQUEST_STATE_ID_PROCESSING === stateCode && checkRoles('SuperAdministrator', 'Administrator', 'Supervisor') && <LoadingButton style={style} variant={"contained"} loading={workflowLoading}
                    onClick={() => setStatus(REQUEST_STATE_ID_PROCESSING, REQUEST_STATE_ID_RESEARCH)}>Назначить исполнителя</LoadingButton>}
                  {REQUEST_STATE_ID_PROCESSING === stateCode && checkRoles('SuperAdministrator', 'Administrator', 'Supervisor') && (
                    <Stack
                      key="direction-reject-processing"
                      direction="column"
                      alignItems="flex-start"
                      spacing={0.5}
                      sx={{ ml: 1.25, maxWidth: 320 }}
                    >
                      <LoadingButton
                        style={style}
                        variant={"contained"}
                        color={"warning"}
                        loading={workflowLoading}
                        disabled={rejectBlockedBySignedProtocol}
                        onClick={() => {
                          if (rejectBlockedBySignedProtocol) {
                            return;
                          }
                          commentDialog('Изменение статуса', 'Укажите комментарий для отклонения.', (_) => {
                            setStatus(REQUEST_STATE_ID_PROCESSING, REQUEST_STATE_ID_DECLINED, null, _?.comment);
                          });
                        }}
                      >
                        Отклонить с комментариями
                      </LoadingButton>
                      {rejectBlockedBySignedProtocol ? (
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                          По данному направлению уже есть подписанный протокол.
                        </Typography>
                      ) : null}
                    </Stack>
                  )}
                  {REQUEST_STATE_ID_RESEARCH === stateCode && checkRoles('SuperAdministrator', 'Administrator', 'Supervisor') && (
                    <Stack
                      key="direction-reject-research"
                      direction="column"
                      alignItems="flex-start"
                      spacing={0.5}
                      sx={{ ml: 1.25, maxWidth: 320 }}
                    >
                      <LoadingButton
                        style={style}
                        variant={"contained"}
                        color={"warning"}
                        loading={workflowLoading}
                        disabled={rejectBlockedBySignedProtocol}
                        onClick={() => {
                          if (rejectBlockedBySignedProtocol) {
                            return;
                          }
                          commentDialog('Изменение статуса', 'Укажите комментарий для отклонения.', (_) => {
                            setStatus(REQUEST_STATE_ID_RESEARCH, REQUEST_STATE_ID_DECLINED, null, _?.comment);
                          });
                        }}
                      >
                        Отклонить с комментариями
                      </LoadingButton>
                      {rejectBlockedBySignedProtocol ? (
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                          По данному направлению уже есть подписанный протокол.
                        </Typography>
                      ) : null}
                    </Stack>
                  )}
                  {REQUEST_STATE_ID_RESEARCH === stateCode && checkRoles('SuperAdministrator', 'Administrator', 'Performer') && <LoadingButton style={style} variant={"contained"} loading={workflowLoading}
                    onClick={() => setStatus(REQUEST_STATE_ID_RESEARCH, REQUEST_STATE_ID_COMPLETED)}>Завершить</LoadingButton>}
                  {(REQUEST_STATE_ID_RESEARCH === stateCode) && checkRoles('SuperAdministrator', 'Administrator', 'Performer') && <LoadingButton
                    style={style} variant={"outlined"} loading={workflowLoading}
                    onClick={() => setCreateResultOpen(true)}
                  >Добавить запись в журнал</LoadingButton>}
                  {REQUEST_STATE_ID_RESEARCH === stateCode && checkRoles('SuperAdministrator', 'Administrator', 'Performer') && <Tooltip title={!hasResults ? "Необходима запись в журнале" : "Создание протокола"}>
                    <div style={style}>
                      <LoadingButton variant={"outlined"} loading={workflowLoading} onClick={() => setProtocolAddOpen(true)} disabled={!hasResults}>Создать протокол</LoadingButton>
                    </div>
                  </Tooltip>}
                  {REQUEST_STATE_ID_DECLINED === stateCode && checkRoles('SuperAdministrator', 'Administrator', 'Specialist', "Committee", "CommitteeAdministrator") && <LoadingButton style={style} variant={"contained"} loading={workflowLoading} onClick={() => setStatus(REQUEST_STATE_ID_DECLINED, REQUEST_STATE_ID_CREATED)}>Пересоздать</LoadingButton>}
                  {REQUEST_STATE_ID_DECLINED === stateCode && checkRoles('SuperAdministrator', 'Administrator', 'Specialist', "Committee", "CommitteeAdministrator") && <LoadingButton style={style} variant={"contained"} loading={workflowLoading} onClick={() => setDeleteActOpen(true)} color={"warning"}>Удалить</LoadingButton>}
                </Toolbar>
              </Paper>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <TableContainer component={Paper}>
                  <Table aria-label="simple table">
                    <TableBody className="border-table-word">
                      <TableRow>
                        <TableCell className="border-table-word"></TableCell>
                        <TableCell
                          className="border-table-word"
                          align="left"
                        >
                          <p>Нысанның БҚСЖ бойынша коды</p>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <p style={{ width: "50%" }}>Код формы по ОКУД</p>
                            <TextField
                              id="outlined-basic"
                              label="Код"
                              style={{ width: "50%" }}
                              variant="outlined"
                            />
                          </div>
                          <p>КҰЖЖ бойынша ұйым коды</p>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <p style={{ width: "50%" }}>Код организации по ОКПО</p>
                            <TextField
                              id="outlined-basic"
                              label="Код"
                              style={{ width: "50%" }}
                              variant="outlined"
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="border-table-word">
                          <p>
                            Қазақстан Республикасының Денсаулық сақтау министрлігі
                            Министерство здравоохранения Республики Казахстан
                          </p>
                        </TableCell>
                        <TableCell
                          className="border-table-word"
                          align="left"
                        >
                          <p>
                            Қазақстан Республикасының Денсаулық сақтау министрінің
                            2021 жылғы "20" 08 № 84 бұйрығымен бекітілген № {directionFormNumber}/е
                            нысанды медициналық құжаттама
                          </p>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="border-table-word">
                          <p>
                            Санитариялық-эпидемиологиялық қызметтiң мемлекеттiк
                            органының атауы Наименование государственного органа
                            санитарно-эпидемиологической службы
                          </p>
                        </TableCell>
                        <TableCell
                          className="border-table-word"
                          align="left"
                        >
                          <p>
                            Медицинская документация Форма № {directionFormNumber}/у Утверждена
                            приказом Министра здравоохранения Республики Казахстан
                            от "20" 08 2021 года №84
                          </p>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
              {!isNew && !state.isCreated() && !state.isProcessing() && <FormSection heading={"Журналы и протоколы"} mb={4}>
                <ResultsAndProtocolsSection
                  results={data?.results}
                  protocols={data?.protocols}
                  onResultClick={async (_) => {
                    await handleEditResultOpen(_);
                  }}
                  onProtocolClick={async (_) => {
                    await handleEditProtocolOpen(_);
                  }}
                  resultsReloadValue={reloadValue}
                  fetch={fetch}
                  startLoading={async () => {
                    await startSubmit();
                  }}
                  stopLoading={async () => {
                    await stopSubmit();
                  }}
                />
              </FormSection>}
              <div style={{ margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                <h5 align='center'>
                  {directionFormKazakhName}
                </h5>
                <h5 align='center'>
                  {directionFormName} (от)
                </h5>
                <LocalizationProvider locale={ruLocale} dateAdapter={AdapterDateFns}>
                  <DatePicker views={['day']} label="от"
                    disabled={isDisabledData}
                    value={data.data?.dateOfDirection?.value}
                    onChange={(value) => {
                      handleChangeData('dateOfDirection', value, 'dateOfDirection')
                    }}
                     renderInput={(params) =>
                      <TextField margin="normal"  {...params} helperText={null} />
                    } />
                </LocalizationProvider>
              </div>

              {stateCode == REQUEST_STATE_ID_DECLINED && <>
                  <label>Комментарий: {data.comment}</label>
              </>}

              {data.number && <h5 style={{ marginBottom: "30px" }} align="center">
                №{data.number}
              </h5>}
              {REQUEST_STATE_ID_RESEARCH === stateCode  && <><ResearchListField
                type={orderType == orderTypeIds.government ? SERVICE_TYPE_GOVERNMENT : SERVICE_TYPE_PAYED}
                label='Список Исследований'
                value={data.specifications}
                onChange={(val) => handleChange("specifications", val)}
              />
                {renderDateField(
                  "researchEndDateTime",
                  "Дата окончания исследования (цены тарификатора зависят от этой даты)",
                  12,
                  {}
                )}
              </>}
              {REQUEST_STATE_ID_RESEARCH === stateCode && orderType == orderTypeIds.government && (
                <FormSection mb={4}>
                  <Typography component="h4" align="left" variant="h6">
                    Услуги ({specifications?.length || 0})
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <BasicTable
                        order="asc"
                        cells={[
                          { id: "id", label: t("specification.tableItem.id") },                          
                          { id: "service", label: t("specification.tableItem.service"), render: ({ service }) => service?.name },
                          { id: "unit", label: t("specification.tableItem.unit"), render: ({ unit }) => unit?.name || unit?.shortName },
                          { id: "schedule", label: t("specification.tableItem.schedule"), render: ({ schedule }) => schedule?.name },
                          {
                            id: "group",
                            label: 'Группа',
                            render: ({ service }) => service?.group?.name,
                          },
                          {
                              id: "method",
                              label: 'Метод',
                              render: ({ service }) => service?.kind?.name,
                          },                        
                        ]}
                        rows={specifications}
                        disabled
                      />
                    </Grid>
                  </Grid>
                </FormSection>
              )}
              {REQUEST_STATE_ID_COMPLETED === stateCode  && <><ResearchListField
                label='Список Исследований'
                value={data.specifications}
                onChange={(val) => handleChange("specifications", val)}
                disabled={true}
              />
                {renderDateField(
                  "researchEndDateTime",
                  "Дата окончания исследования (цены тарификатора зависят от этой даты)",
                  12,
                  {}
                )}
              </>}
              <PatientField
                  object={data.targetPatient}
                  value={data.targetPatientId}
                  onChange={handleChangePatient}
              />
              <DirectionForm addSample={addSample}
                handleChangeSample={handleChangeSample}
                isDisabledData={isDisabledData} data={data}
                handleChangeData={handleChangeData}
                handleChangeMulti={handleChangeMulti}
                deleteSample={deleteSample} />
              <EmployeeField
                style={{ marginBottom: "5px" }}
                label={"Ответственный за отбор проб"}
                value={data.responsibleForProbesId}
                disabled={isDisabledData}
                object={data.responsibleForProbes} onChange={(val) => {
                  setData({ ...data, responsibleForProbesId: val?.id, responsibleForProbes: val })
                }}
              />
                <ContractSelect
                  label={"Договор КСЭК"}
                  disabled={isDisabledData}
                  year={(new Date()).getFullYear()}
                  fullWidth
                  value={data.governmentContractId}
                  onChange={(val, obj) => {
                    handleChangeMulti({
                      governmentContractId: val,
                      governmentContract: obj
                    });
                  }}
                />
              <FilialSelect
                style={{ marginBottom: "5px" }}
                label={"Филиал"}
                fullWidth
                disabled={isDisabledData}
                //filialId={checkRoles('AdministratorReadonly') ? null : JSON.parse(localStorage.employee)?.filialId}
                value={data.filialId}
                organizationId={isCommittee() ? organizationIds.nce : null}
                onChange={(val) => {
                  handleChangeMulti({
                    filialId: val,
                    filialDivisionId: Number(val) !== Number(data.filialId) ? null : data.filialDivisionId
                  })
                }}
              />
              <FilialDivisionSelect2
                style={{ marginBottom: "5px" }}
                label={"Районное отделение"}
                fullWidth
                disabled={isDisabledData}
                value={data.filialDivisionId}
                filialId={data.filialId === 1 ? null : data.filialId}
                onChange={(filialDivisionId, filialDivision) => {
                  handleChangeMulti({
                    filialDivisionId: filialDivisionId,
                    filialId:
                      filialDivision != null &&
                      Number(filialDivision.filialId) !== Number(data.filialId)
                        ? filialDivision.filialId
                        : data.filialId
                  })
                }}
              />
              {orderType == orderTypeIds.government && <Grid item xs={12}>
                <RHFFormProvider {...indicatorsMethods}>
                  <RHFIndicator disabled={isDisabledData} />
                </RHFFormProvider>
              </Grid>}
              <Typography gutterBottom variant={'h5'}>Передача проб в лабораторию:</Typography>
              <DepartmentSelect
                disabled={isDisabledData}
                style={{ marginBottom: "5px" }}
                label={"Лаборатория"}
                fullWidth
                value={data.departmentId}
                onChange={(val) => handleChange('departmentId', val)}
                hideParasitology={true}
              />
              <DivisionSelect
                style={{ marginBottom: "5px" }}
                label={"Отдел"}
                fullWidth
                value={data.divisionId}
                disabled={isDisabledData}
                onChange={(val) => handleChange('divisionId', val)}
                filterOptions={(opt) => !data.departmentId || (Number(opt.departmentId) === Number(data.departmentId))}
              />
              <EmployeeField
                label={"Заведующий лаборатории/Начальник отдела"}
                fullWidth
                extraFilter={data.departmentId > 0 ? { departmentId: { operand1: data.departmentId, operator: "equals" } } : null}
                disabled={isDisabledData}
                object={data.responsible}
                value={data.responsibleId}
                onChange={(responsible) => {
                  if (!responsible) {
                    handleChangeMulti({
                      responsible: null,
                      responsibleId: null,
                    });
                    return;
                  }
                  const obj = {
                    responsible,
                    responsibleId: responsible.id,
                    departmentId: responsible.departmentId,
                  };
                  handleChangeMulti(obj);
                }}
              />
              <EmployeeField
                label={"Лаборант"} fullWidth
                object={data.performer}
                value={data.performerId}
                disabled={isDisabledPerformer}
                onChange={(performer) => {
                  if (!performer) {
                    handleChangeMulti({
                      performer: null,
                      performerId: null,
                    });
                    return;
                  }
                  const obj = {
                    performer,
                    performerId: performer.id,
                  };
                  handleChangeMulti(obj);
                }}
              />
              <LoadingButton style={{
                border: "1px solid black",
                padding: "5px 10px",
                borderRadius: "6px",
                marginRight: '15px'
              }}
                variant="contained"
                // disabled={isDisabledData}
                onClick={submit}
                loading={loadingSubmit}>
                {isNew ? "Создать" : "Сохранить"}
              </LoadingButton>
            </Box>
            {
              createResultOpen && <DirectionResult
                title={'Создание журнала направления'}
                onClose={() => setCreateResultOpen(false)}
                onSubmit={(response) => {
                  fetch();
                }}
                act={data}
              />
            }
            <EditResultAndProtocolSection
              editResultOpen={editResultOpen}
              setEditResultOpen={setEditResultOpen}
              editProtocolOpen={editProtocolOpen}
              setEditProtocolOpen={setEditProtocolOpen}
              editResultId={editResultId}
              editProtocol={editProtocol}
              startLoading={async () => {
                await startSubmit();
              }}
              stopLoading={async () => {
                await stopSubmit();
              }}
              onEditResultSubmitted={async (_) => {
                reload();
              }}
              results={data.results}
            />
            {
              deleteActOpen && <RemovePopup
                deleteAction={() => actCRUD.delete(id)}
                title={`Вы действительно хотите удалить направление №${data.number}?`}
                onClose={() => setDeleteActOpen(false)}
                onSubmit={() => push(`/${directionName}`)}
              />
            }
            {
              protocolAddOpen && <CreateProtocol
                action={"createByAct"}
                open={protocolAddOpen}
                initNumber={data.number}
                title={`Создание протокола для направления №${data.number}?`}
                onClose={() => setDeleteActOpen(false)}
                onSubmit={(data) => {
                  return dispatch(addProtocol(data, { "sort": { "id": { "operator": "asc" } } }))
                    .then(fetch);
                }}
                setOpen={setProtocolAddOpen}
                request={data}
                orderType={data.orderType || orderType}
              />
            }
            <Dialog
              open={protocolViewOpen}
              onClose={() => setProtocolViewOpen(false)}
              fullWidth
            >
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Document
                      file={protocolViewPdf}
                      onLoadSuccess={onDocumentLoadSuccess}
                    >
                      <Page pageNumber={pageNumber} />
                    </Document>
                  </Grid>
                </Grid>
              </Box>
            </Dialog>
        </>}
      </DashboardLayout >
    );
  };
}

export default withDirectionDetail